import { randomUUID } from '../../../../../utils/uuid';
import { type RouterSetupOptionsResponse, type ShortcutsAccess } from '../../../../../utils/api/schema/shortcutSchema';
import { type RouterProtocol } from './RouterProtocolBlock';
import optimalLocationIcon from '../../../../../../assets/icons/devices-router/optimal-location-icon.svg';

const protocolKeyMap: Record<RouterProtocol, string> = {
  OpenVPN: 'OpenVPN_TCP',
  WireGuard: 'WireGuard',
};

export const protocolResponseMap: Record<string, RouterProtocol> = {
  openvpn: 'OpenVPN',
  wireguard: 'WireGuard',
};

export const configFilesGroup = {
  COUNTRIES: 'countries',
  STREAMING: 'streaming',
} as const;

export type ConfigGroupType = (typeof configFilesGroup)[keyof typeof configFilesGroup];

export interface ConfigOption {
  uuid: string;
  shortcut_group_id: string;
  shortcut_id: string;
  shortcuts_access: ShortcutsAccess;
  title: string;
  imageUrl: string;
  suboptions: Array<{ title: string; cluster_id: string }>;
  isOptimalLocation?: boolean;
}

export interface ConfigOptions {
  shortcut_group_id: string;
  options: ConfigOption[];
}

export type RouterConfigOptions = Record<ConfigGroupType, ConfigOptions>;

const emptyOptions = (): RouterConfigOptions => ({
  countries: { shortcut_group_id: '', options: [] },
  streaming: { shortcut_group_id: '', options: [] },
});

const transformClusters = (
  clusters: RouterSetupOptionsResponse['shortcut_groups'][number]['shortcuts'][number]['clusters'],
  protocol: RouterProtocol,
) => {
  const res = clusters
    .filter((c) => c.protocols.some((p) => p.protocol === protocolKeyMap[protocol]))
    .map((cluster) => ({
      title: cluster.location.city.en,
      cluster_id: cluster.id,
    }));

  return res;
};

export const parseRouterSetupOptions = (
  response: RouterSetupOptionsResponse,
  optimalLocationTitle: string,
  protocol: RouterProtocol,
): RouterConfigOptions => {
  const preparedOptions = emptyOptions();

  const shortcutGroupMatchers: Record<
    ConfigGroupType,
    (group: RouterSetupOptionsResponse['shortcut_groups'][number]) => boolean
  > = {
    [configFilesGroup.COUNTRIES]: (group) => group.shortcut_type === 'SHORTCUTS_TYPE_COUNTRY_PICKER',
    [configFilesGroup.STREAMING]: (group) => group.shortcut_type === 'SHORTCUTS_TYPE_STREAMING',
  };

  Object.entries(shortcutGroupMatchers).forEach(([key, matchGroup]) => {
    const group = response.shortcut_groups.find((g) => matchGroup(g));

    if (!group) {
      return;
    }

    const groupEntry: ConfigOptions = {
      shortcut_group_id: group.group_id,
      options: group.shortcuts.map((shortcut) => ({
        uuid: randomUUID(),
        shortcut_group_id: group.group_id,
        shortcut_id: shortcut.id,
        shortcuts_access: shortcut.shortcuts_access,
        title: shortcut.title.en,
        imageUrl: shortcut.images.icon,
        suboptions: transformClusters(shortcut.clusters, protocol),
      })),
    };

    // exclude shortcuts that have no matching clusters
    groupEntry.options = groupEntry.options.filter((option) => option.suboptions.length > 0);

    // exclude groups that ended up with no shortcuts
    if (groupEntry.options.length > 0) {
      preparedOptions[key as ConfigGroupType] = groupEntry;
    }
  });

  const optimalLocationOption: ConfigOption = {
    uuid: randomUUID(),
    shortcut_group_id: preparedOptions.countries.shortcut_group_id,
    shortcut_id: response.optimal_location.id,
    shortcuts_access: response.optimal_location.shortcuts_access,
    title: optimalLocationTitle,
    imageUrl: optimalLocationIcon,
    isOptimalLocation: true,
    suboptions: [
      {
        title: response.optimal_location.cluster.location.city.en,
        cluster_id: response.optimal_location.cluster.id,
      },
    ],
  };

  preparedOptions.countries.options.unshift(optimalLocationOption);

  return preparedOptions;
};
